import { SiteUrls } from '@tableau/shared-urls';
import { SagaIterator } from 'redux-saga';
import { call, put, select, takeLatest } from 'redux-saga/effects';

import { ContentActionsService } from '~commonInfra/contentActions/ContentActions';
import { ReactModalService } from '~commonInfra/dialogs/ReactModalService';
import { FeatureFlags } from '~commonInfra/featureFlags/FeatureFlags';
import { WindowLocationService } from '~commonInfra/services/WindowLocationService';
import {
  CreateNewContentInfoRequest,
  CreateNewFlow,
  CreateNewLens,
  CreateNewPredictiveModel,
  CreateNewProject,
  CreateNewPublishedConnection,
  CreateNewPublishedDataSource,
  CreateNewSchedule,
  CreateNewSite,
  CreateNewWorkbook
} from '~src/bootstrap/redux/actions/AppActionTypes';
import { requestFailed } from '~src/bootstrap/redux/actions/RequestFailedAction';
import { SessionSiteSelectors } from '~src/bootstrap/redux/selectors/SessionInfoSelectors';
import { Messages } from '~src/codegen/Localize';
import { IResult } from '~src/httpClient/serverApi/types/IResponse';
import { SliceableHelper } from '~src/httpClient/utils/SliceableHelper';
import { WebHttpClientWithErrorToastInstance } from '~src/httpClient/WebHttpClientWithErrorToast';
import { LensesTAGHandler } from '~src/lens/api/LensTAG';
import { createLensDialog } from '~src/lens/LensCreationDialog/LensCreationDialog';
import { DestinationProjectMapper } from '~src/projects/models/mappers/DestinationProjectMapper';
import { DestinationProjects } from '~src/projects/resources/DestinationProjects';
import { Projects } from '~src/projects/resources/Projects';
import { AdminSelfServiceScheduleDialog, AdminSelfServiceScheduleDialogState } from '~src/schedule/components/AdminSelfServiceScheduleDialog';
import { CreateOrUpdateScheduleActionService } from '~src/schedule/services/CreateOrUpdateScheduleAction';
import { CreateScheduleRequest } from '~src/schedule/types/CreateScheduleRequest';
import { CreateSiteAction } from '~src/siteManagement/services/CreateSiteAction';
import { SpaceInfoItemTypeValue } from '~src/spacesPlatform/models/ContentType';
import { IContentInfo } from '~src/spacesPlatform/models/IContentInfo';
import { IExplorableItemTypeInfo, ISpaceTypeInfo } from '~src/spacesPlatform/models/resourceInfoParts';
import { selectSpaceInfo, selectSpaceInfoItem } from '~src/spacesPlatform/selectors/SpaceInfoSelectors';
import { newWorkbookForDatasource } from '~src/workbook/actions/NewWorkbookForDatasourceAction';

import { requestAvailableActionsForEntity } from '../actions/AvailableActionsForEntityActions';
import {
  CreateNewContentInfoRequestAction,
  CreateNewFlowAction,
  CreateNewLensAction,
  CreateNewPredictiveModelAction,
  CreateNewProjectAction,
  CreateNewPublishedConnectionAction,
  CreateNewPublishedDatasourceAction,
  CreateNewScheduleAction,
  CreateNewSiteAction,
  CreateNewWorkbookAction
} from '../actions/ContentExplorerCreateActions';
import { requestSpaceContents } from '../actions/RequestSpaceContentsActions';
import { SpaceInfo } from '../state/SpaceInfo';

export function* handleCreateNewContentInfoRequest(action: CreateNewContentInfoRequestAction) {
  // if app state doesnt have all the data it needs, make those API calls here.
  try {
    const spaceInfo: SpaceInfo = yield select(selectSpaceInfo);
    yield put(requestAvailableActionsForEntity(spaceInfo.item as IExplorableItemTypeInfo));
  } catch(e) {
    yield put(requestFailed(e, Messages.errorUnknown()));
  }
}

// Create New Workbook

const createNewWorbook = (siteUrlName: string, projectId: string) => {
  ContentActionsService.newEmptyWorkbook(siteUrlName, projectId);
};

export function* handleCreateNewWorkbook(action: CreateNewWorkbookAction) {
  try {
    const siteUrlName: string = yield select(SessionSiteSelectors.siteUrlName);
    // If we are on a datasource space, we should create new workbook for the datasource
    const spaceItem: ISpaceTypeInfo = yield select(selectSpaceInfoItem);
    if (spaceItem.contentType === SpaceInfoItemTypeValue.datasource) {
      yield put(newWorkbookForDatasource(spaceItem as IContentInfo));
      return;
    }
    yield call(createNewWorbook, siteUrlName, action.projectId || '');
  } catch (e) {
    yield put(requestFailed(e, Messages.errorUnknown()));
  }
}

// Create New Project

const createNewProject = (projectId: string): Promise<IResult> => {
  return ContentActionsService.createProject(projectId);
};

export function* handleCreateNewProject(action: CreateNewProjectAction) {
  try {
    yield call(createNewProject, action.projectId);

    yield put(requestSpaceContents());
  } catch (e) {
    yield put(requestFailed(e, Messages.errorUnknown()));
  }
}

function* getProjectLuid(projectId: string) {
  const projectInfo = yield call(Projects.getById.bind(Projects), projectId);
  return projectInfo.luid;
}

// Create new published datasource

const createNewPublishedDatasource = (siteUrlName: string, projectId: string) => {
  ContentActionsService.generateTempWorkbookForCreateDatasource(siteUrlName, projectId);
};

export function* handleCreateNewPublishedDataSource(action: CreateNewPublishedDatasourceAction) {
  try {
    const siteUrlName: string = yield select(SessionSiteSelectors.siteUrlName);
    yield call(createNewPublishedDatasource, siteUrlName, action.projectId || '');
  } catch (e) {
    yield put(requestFailed(e, Messages.errorUnknown()));
  }
}

// Create new Published connection
export function* handleCreateNewPublishedConnection(action: CreateNewPublishedConnectionAction) {
  try {
    const contentActionsService = ContentActionsService;
    if (action.projectId) {
      const projectLuid = yield call(getProjectLuid, action.projectId);
      contentActionsService.newPublishedConnection(projectLuid);
    } else {
      contentActionsService.newPublishedConnection();
    }
  } catch (e) {
    yield put(requestFailed(e, Messages.errorUnknown()));
  }
}

// Create New Flow
export function* handleCreateNewFlow(action: CreateNewFlowAction) {
  try {
    const siteUrlName: string = yield select(SessionSiteSelectors.siteUrlName);
    const spaceInfo: SpaceInfo = yield select(selectSpaceInfo);
    ContentActionsService.newFlow(siteUrlName, spaceInfo.item, action.projectId);
  } catch (e) {
    yield put(requestFailed(e, Messages.errorUnknown()));
  }
}

// Create New Lens
export function* handleCreateNewLens(action: CreateNewLensAction) {
  const projectsQuery = DestinationProjects.getQueryForSpecificSourceContentAndMatchText(
    'project',
    [action.projectId],
    '',
  );
  const dsProjectValid = yield SliceableHelper.fetchAll(projectsQuery).then(projects => {
    if (projects.items[0]) {
      const destinationProjects = DestinationProjectMapper.map(projects.items);
      return destinationProjects[0].status;
    }
  });

  if (dsProjectValid === 'valid') {
    const siteUrlName: string = yield select(SessionSiteSelectors.siteUrlName);
    const siteUrl = new SiteUrls({siteUrlName});

    try {
      const lensResponse = yield LensesTAGHandler.createLens({
        name: action.datasourceName,
        description: action.datasourceDescription,
        project_id: parseInt(action.projectId),
        datasource_luid: action.datasourceLuid,
      });

      const lensUrl = siteUrl.lensUrl(lensResponse.id);
      localStorage.setItem(lensResponse.luid!, JSON.stringify({
        lensCuration: true,
      }));
      WindowLocationService.open(lensUrl, '_blank');
    } catch (e) {
      if (e.httpErrorCode === 409) {
          const spaceInfo: SpaceInfo = yield select(selectSpaceInfo);
          const publishedDatasourceId = action.publishedDatasourceId;
          ReactModalService.openCustom(createLensDialog(siteUrlName, spaceInfo, siteUrl, publishedDatasourceId, action.datasourceLuid));
      } else {
        yield put(requestFailed(e, Messages.errorUnknown()));
      }
    }
    return;
  }

  try {
    const siteUrlName: string = yield select(SessionSiteSelectors.siteUrlName);
    const spaceInfo: SpaceInfo = yield select(selectSpaceInfo);
    const siteUrl = new SiteUrls({siteUrlName});
    const publishedDatasourceId = action.publishedDatasourceId;
    ReactModalService.openCustom(createLensDialog(siteUrlName, spaceInfo, siteUrl, publishedDatasourceId, action.datasourceLuid));
  } catch (e) {
    yield put(requestFailed(e, Messages.errorUnknown()));
  }
}

// Create New PredictiveModel
const createNewPredictiveModel = (siteUrlName: string) => {
  return ContentActionsService.newPredictiveModel(siteUrlName);
};

export function* handleCreateNewPredictiveModel(action: CreateNewPredictiveModelAction) {
  try {
    const siteUrlName: string = yield select(SessionSiteSelectors.siteUrlName);
    yield call(createNewPredictiveModel, siteUrlName);
  } catch (e) {
    yield put(requestFailed(e, Messages.errorUnknown()));
  }
}

// Create New Site
const createNewSite = (): Promise<IResult> => {
  return new CreateSiteAction().execute();
};

export function* handleCreateNewSite(action: CreateNewSiteAction) {
  try {
    yield call(createNewSite);
    yield put(requestSpaceContents());
  } catch (e) {
    yield put(requestFailed(e, Messages.errorUnknown()));
  }
}

// Create new schedule

const createNewSchedule = (type?: string, isEnabledForLinkedTasks?: boolean): Promise<IResult> => {
  if (FeatureFlags.adminSelfServiceSchedulesEnabled) {
    return AdminSelfServiceScheduleDialog.open((scheduleState: AdminSelfServiceScheduleDialogState) => {
      const request = new CreateScheduleRequest({
        name: scheduleState.name,
        scheduleType: scheduleState.recurrence.recurrenceType,
        scheduleDetails: scheduleState.recurrence.recurrenceDetails,
        scheduledAction: scheduleState.type,
        priority: scheduleState.priority,
        parallel: scheduleState.isParallel,
        enabledForLinkedTasks: scheduleState.enabledForLinkedTasks && scheduleState.isParallel,
      });

      return new Promise<IResult>((resolve, reject) => {
        WebHttpClientWithErrorToastInstance.sendRequest(request).then(result => {
          CreateOrUpdateScheduleActionService.notifyResult(result, scheduleState.name);
          return resolve(result);
        }).catch(err => reject(err));
      });
    }, undefined, undefined, type, undefined, isEnabledForLinkedTasks);
  } else {
    return new Promise<IResult>((resolve, reject) => {
      CreateOrUpdateScheduleActionService.createSchedule().then(val => resolve(val)).catch(err => reject(err));
    });
  }
};

export function* handleCreateNewSchedule(action: CreateNewScheduleAction) {
  try {
    yield call(createNewSchedule, action.scheduleType, action.isEnabledForLinkedTasks);
    yield put(requestSpaceContents());
  } catch (e) {
    yield put(requestFailed(e, Messages.errorUnknown()));
  }
}
export function* rootContentCreationSaga(): SagaIterator {
  yield takeLatest(CreateNewContentInfoRequest, handleCreateNewContentInfoRequest);
  yield takeLatest(CreateNewWorkbook, handleCreateNewWorkbook);
  yield takeLatest(CreateNewProject, handleCreateNewProject);
  yield takeLatest(CreateNewPublishedConnection, handleCreateNewPublishedConnection);
  yield takeLatest(CreateNewFlow, handleCreateNewFlow);
  yield takeLatest(CreateNewPredictiveModel, handleCreateNewPredictiveModel);
  yield takeLatest(CreateNewLens, handleCreateNewLens);
  yield takeLatest(CreateNewSite, handleCreateNewSite);
  yield takeLatest(CreateNewSchedule, handleCreateNewSchedule);
  yield takeLatest(CreateNewPublishedDataSource, handleCreateNewPublishedDataSource);
}
